import { useEffect, useState, useRef, useCallback, useMemo, memo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../layouts/Main';
import config from '../../../config';
import { toast } from 'react-toastify';
import { jwtDecode } from 'jwt-decode';
import Select from 'react-select';
import { PulseLoader } from "react-spinners";
import { Badge, Card, Alert } from 'react-bootstrap';
import PaymentTermsSelect from '../../common/PaymentTermsSelect';
import CompanyAddressSelect from '../../common/CompanyAddressSelect';
import ProductSelector from '../../common/ProductSelector';

// Memoized item card component to prevent unnecessary re-renders
const RFQItemCard = memo(({ item, idx, onProductSelect, onItemChange, onRemove, canRemove }) => {
    return (
        <div className="card p-3 mb-3 border">
            <div className="row g-2">
                <div className="col-md-4">
                    <label className="form-label">Product *</label>
                    <ProductSelector
                        value={item.product?.value || item.product || ''}
                        onProductChange={(selectedOption) => onProductSelect(idx, selectedOption)}
                        placeholder="Select product"
                    />
                </div>

                <div className="col-md-3">
                    <label className="form-label">Quality</label>
                    <input
                        className="form-control"
                        value={item.quality}
                        onChange={(e) => onItemChange(idx, 'quality', e.target.value)}
                        placeholder="e.g., Grade A"
                    />
                </div>

                <div className="col-md-3">
                    <label className="form-label">Quantity *</label>
                    <div style={{ position: 'relative' }}>
                        <input
                            className="form-control pe-5"
                            type="number"
                            step="1"
                            value={item.required_quantity}
                            onChange={(e) => onItemChange(idx, 'required_quantity', e.target.value)}
                            required
                        />
                        {item.quantity_unit && (
                            <span
                                className='unit-suffix'
                            >
                                {item.quantity_unit}
                            </span>
                        )}
                    </div>
                </div>

                <div className="col-md-2 align-content-center text-end">
                    {canRemove && (
                        <>
                        <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => onRemove(idx)}
                        >
                            <i className="fas fa-trash"></i>
                        </button>
                        </>
                    )}
                </div>

                {/* Show source PRs for consolidated items */}
                {item.source_prs && item.source_prs.length > 1 && (
                    <div className="col-12">
                        <div className="alert alert-info py-2 mb-0">
                            <small>
                                <strong>📊 Consolidated from:</strong>
                                {item.source_prs.map((source, i) => (
                                    <span key={`${source.pr_id}-${i}`}>
                                        {i > 0 && ', '}
                                        <Badge bg="secondary" className="mx-1">
                                            {source.pr_no}: {source.quantity} {item.quantity_unit}
                                        </Badge>
                                    </span>
                                ))}
                            </small>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
});

const RFQCreatePage = () => {
    const { requestId } = useParams();
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const isMountedRef = useRef(true);

    const [form, setForm] = useState({
        rfq_type: requestId ? 'individual' : 'consolidated', // individual or consolidated
        selected_purchase_requests: [],
        delivery_location: '',
        payment_terms: '',
        delivery_date: '',
        note: '',
        items: [],
        vendor_ids: [],
        broker_ids: []
    });

    // Today's date in YYYY-MM-DD format for date input min attr
    const todayDateISO = useMemo(() => new Date().toISOString().split('T')[0], []);

    const [vendors, setVendors] = useState([]);
    const [brokers, setBrokers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [availablePurchaseRequests, setAvailablePurchaseRequests] = useState([]);
    const [selectedPRDetails, setSelectedPRDetails] = useState([]);
    // const [paymentTermsOptions, setPaymentTermsOptions] = useState([]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // Fetch vendors, brokers, payment terms, and available purchase requests
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                const [
                    vendorRes, 
                    brokerRes, 
                    // settingsRes
                ]
                     = await Promise.all([
                    axios.get(`${config.API_BASE_URL}/vendors?approved=true&status=Active`),
                    axios.get(`${config.API_BASE_URL}/brokers?approved=true&status=Active`),
                    // axios.get(`${config.API_BASE_URL}/company-settings`)
                ]);

                setVendors(vendorRes.data || []);
                setBrokers(brokerRes.data || []);

               
                // const settings = settingsRes.data || {};
                // const paymentTerms = settings.paymentTerms?.options || ['100% Advance', '50% Advance, 50% Before Delivery', '100% Against Delivery', 'Net 15 days', 'Net 30 days', 'Net 60 days', 'Net 90 days', 'Cash on Delivery'];
                // const paymentOptions = paymentTerms.map(term => ({
                //     value: term,
                //     label: term
                // }));
                // setPaymentTermsOptions(paymentOptions);

                if (requestId) {
                    const reqRes = await axios.get(`${config.API_BASE_URL}/purchase-requests/${requestId}`);
                    const prData = reqRes.data;
                    
                    setForm(prev => ({
                        ...prev,
                        rfq_type: 'individual',
                        selected_purchase_requests: [{ value: prData._id, label: prData.request_no }],
                        delivery_location: prData.delivery_location || '',
                        items: (prData.items || []).map(item => ({
                            product: item.product
                                ? { value: item.product._id, label: item.product.productName }
                                : null,
                            product_name: item.product?.productName || '',
                            unit: item.product?.unitId?.unitCode || '',
                            quality: item.quality || '',
                            required_quantity: item.approx_quantity || '',
                            quantity_unit: item.quantity_unit || '',
                            uid: generateUid(),
                            _id: item._id,
                            purchase_request_id: prData._id,
                            purchase_request_no: prData.request_no
                        })),
                    }));
                    
                    setSelectedPRDetails([prData]);
                } else {
                    // Fetch all purchase requests without RFQs (requested status)
                    const prRes = await axios.get(
                        `${config.API_BASE_URL}/purchase-requests?status=requested&has_rfq=false&limit=1000`
                    );
                    
                    const prs = prRes.data.requests || prRes.data || [];
                    const prOptions = prs.map(pr => {
                        // Calculate total items and their details for the label
                        const totalItems = pr.items?.length || 0;
                        const priority = pr.priority || 'N/A';
                        const itemDetails = pr.items?.slice(0, 2).map(item =>
                            `${item.product?.productName || 'Unknown'}: ${item.approx_quantity || 0} ${item.product?.unitId?.unitCode || ''}`
                        ).join(', ') || 'No items';
                        const moreText = totalItems > 2 ? ` (+${totalItems - 2} more)` : '';

                        return {
                            value: pr._id,
                            label: `${pr.request_no} - ${totalItems} items [${priority}] | ${itemDetails}${moreText}`,
                            data: pr
                        };
                    });
                    
                    setAvailablePurchaseRequests(prOptions);
                }

            } catch (err) {
                console.error("Failed to load initial data:", err);
                toast.error("Failed to load initial data");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [requestId]);

    // Handle purchase request selection (multi-select)
    const handlePRSelection = async (selectedOptions) => {
        if (!selectedOptions || selectedOptions.length === 0) {
            setForm(prev => ({
                ...prev,
                selected_purchase_requests: [],
                items: [],
                delivery_location: '',
                delivery_date: '',
                note: ''
            }));
            setSelectedPRDetails([]);
            return;
        }

        try {
            // Fetch full details for selected PRs
            const prIds = selectedOptions.map(opt => opt.value);

            const prDetailsPromises = prIds.map(id =>
                axios.get(`${config.API_BASE_URL}/purchase-requests/${id}`)
            );

            const prDetailsResponses = await Promise.all(prDetailsPromises);
            const prDetails = prDetailsResponses.map(res => res.data);

            setSelectedPRDetails(prDetails);

            // Aggregate items from all selected PRs
            const aggregatedItems = aggregateItems(prDetails);

            // Set common delivery location (from first PR or combine them)
            const deliveryLocations = prDetails
                .map(pr => pr.delivery_location)
                .filter(loc => loc && loc.trim() !== '');

            const combinedDeliveryLocation = deliveryLocations.length > 0
                ? [...new Set(deliveryLocations)].join('\n--- OR ---\n')
                : '';

            // Set delivery date as the minimum required_by_date from all PRs (converted to YYYY-MM-DD format)
            const deliveryDates = prDetails
                .map(pr => pr.required_by_date)
                .filter(date => date)
                .map(date => new Date(date));

            const minDeliveryDate = deliveryDates.length > 0
                ? new Date(Math.min(...deliveryDates.map(d => d.getTime())))
                : null;

            const formattedDeliveryDate = minDeliveryDate ?
                minDeliveryDate.toISOString().split('T')[0] : '';

            setForm(prev => ({
                ...prev,
                selected_purchase_requests: selectedOptions,
                items: aggregatedItems,
                delivery_location: combinedDeliveryLocation,
                delivery_date: formattedDeliveryDate,
                rfq_type: selectedOptions.length > 1 ? 'consolidated' : 'individual'
            }));

        } catch (err) {
            console.error("Error fetching PR details:", err);
            toast.error("Failed to load purchase request details");
        }
    };

    // Aggregate items from multiple PRs - Sum quantities for same product+quality
    const aggregateItems = (prDetails) => {
        const itemsMap = {};

        prDetails.forEach(pr => {
            (pr.items || []).forEach(item => {
                if (!item.product || !item.product._id) {
                    return;
                }
                
                const productId = item.product._id;
                const quality = item.quality || '';
                const key = `${productId}-${quality}`;
                const quantity = Number(item.approx_quantity) || 0;

                if (itemsMap[key]) {
                    // Same product and quality - sum the quantity
                    itemsMap[key].required_quantity += quantity;
                    
                    // Keep track of which PRs this item comes from
                    itemsMap[key].source_prs.push({
                        pr_id: pr._id,
                        pr_no: pr.request_no,
                        quantity: quantity
                    });
                } else {
                    // New product or quality combination
                    itemsMap[key] = {
                        product: { value: item.product._id, label: item.product.productName },
                        product_name: item.product.productName,
                        quality: quality,
                        required_quantity: quantity,
                        quantity_unit: item.quantity_unit || '',
                        uid: generateUid(),
                        source_prs: [{
                            pr_id: pr._id,
                            pr_no: pr.request_no,
                            quantity: quantity || 0
                        }]
                    };
                }
            });
        });

        // return Object.values(itemsMap);
        return Object.values(itemsMap).map(item => ({
        ...item,
        required_quantity: Math.ceil(item.required_quantity)
    }));
    };

    // Generate a small unique id for list keys to keep React DOM stable
    const generateUid = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2,9)}`;

    // Ensure every item has a stable uid (in case items were created without one)
    useEffect(() => {
        if (!form.items || form.items.length === 0) return;
        const needsUid = form.items.some(it => !it.uid);
        if (needsUid) {
            setForm(prev => ({
                ...prev,
                items: prev.items.map(it => it.uid ? it : { ...it, uid: generateUid() })
            }));
        }
    }, [form.items]);

    const handleFormChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const handleCheckboxChange = (type, id) => {
        const list = form[type];
        const updated = list.includes(id)
            ? list.filter(i => i !== id)
            : [...list, id];
        setForm(prev => ({ ...prev, [type]: updated }));
    };



    const handleProductSelect = useCallback((index, selectedOption) => {
        if (selectedOption) {
            setForm(prev => ({
                ...prev,
                items: prev.items.map((item, idx) =>
                    idx === index
                        ? {
                            ...item,
                            product: { value: selectedOption.value, label: selectedOption.label },
                            product_name: selectedOption.label,
                            quantity_unit: selectedOption.unit || ''
                        }
                        : item
                )
            }));
        }
    }, []);

    const handleItemChange = useCallback((index, field, value) => {
        setForm(prev => ({
            ...prev,
            items: prev.items.map((item, idx) => 
                idx === index 
                    ? { ...item, [field]: value }
                    : item
            )
        }));
    }, []);

    const addItem = useCallback(() => {
        setForm(prev => ({
            ...prev,
            items: [...prev.items, { 
                product: null, 
                product_name: '', 
                quality: '', 
                required_quantity: '', 
                quantity_unit: '',
                source_prs: [],
                uid: generateUid()
            }]
        }));
    }, []);

    const removeItem = useCallback((idx) => {
        if (form.items.length > 1) {
            setForm(prev => ({
                ...prev,
                items: prev.items.filter((_, index) => index !== idx)
            }));
        }
    }, [form.items.length]);

    const handleSubmit = useCallback(async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        
        // Validation
        if (!requestId && form.selected_purchase_requests.length === 0) {
            toast.error("Please select at least one purchase request");
            return;
        }

        if (form.items.length === 0) {
            toast.error("Please add at least one item");
            return;
        }

        if (form.vendor_ids.length === 0 && form.broker_ids.length === 0) {
            toast.error("Please select at least one vendor or broker");
            return;
        }

        setSubmitting(true);

        const token = localStorage.getItem('token');
        const decoded = jwtDecode(token);
        const raisedBy = decoded.userId;

        try {
            const formattedItems = form.items.map(item => ({
                product: item.product?.value || item.product_id || '',
                quality: item.quality || '',
                required_quantity: item.required_quantity || item.approx_quantity || '',
                quantity_unit: item.quantity_unit || '',
            }));

            const payload = {
                rfq_type: form.rfq_type,
                raised_by: raisedBy,
                delivery_location: form.delivery_location,
                note: form.note,
                payment_terms: form.payment_terms,
                delivery_date: form.delivery_date,
                vendor_ids: form.vendor_ids,
                broker_ids: form.broker_ids,
                items: formattedItems
            };

            // Validate delivery_date is not in the past
            if (form.delivery_date) {
                const selected = new Date(form.delivery_date);
                const today = new Date();
                // Zero out time for comparison
                selected.setHours(0,0,0,0);
                today.setHours(0,0,0,0);
                if (selected < today) {
                    toast.error('Delivery date cannot be in the past');
                    setSubmitting(false);
                    return;
                }
            }

            // Always send purchase_requests as an array. If only a single PR or requestId present, include it as a single-element array.
            if (requestId) {
                payload.purchase_requests = [requestId];
                payload.rfq_type = 'individual';
            } else if (form.selected_purchase_requests && form.selected_purchase_requests.length > 0) {
                payload.purchase_requests = form.selected_purchase_requests.map(pr => pr.value);
                payload.rfq_type = form.selected_purchase_requests.length > 1 ? 'consolidated' : 'individual';
            } else {
                // no PRs selected - leave as-is (should be blocked earlier)
            }

            console.log('Sending payload:', payload); // Debug log
            const response = await axios.post(`${config.API_BASE_URL}/rfqs/consolidated`, payload);
            console.log('Response received:', response.data); // Debug log
            // Clear submitting state first
            setSubmitting(false);

            // Show success message immediately
            toast.success("RFQ created successfully");

            // Navigate to list page
            const target = '/rfqs';
            console.log('Navigating to:', target); // Debug log
            
                        // Immediate navigation instead of delayed
                        navigate(target, { replace: true });

            if (!isMountedRef.current) return;

        } catch (err) {
            console.error("Error creating RFQ:", err);
            if (isMountedRef.current) {
                toast.error(err.response?.data?.message || "Failed to create RFQ");
            }
        } finally {
            if (isMountedRef.current) {
                setSubmitting(false);
            }
        }
    }, [requestId, form, navigate, isMountedRef]);

    if (loading) {
        return (
            <Layout>
                <div className="text-center py-5">
                    <PulseLoader color="#014b7cbb" size={9} />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="container">
                <nav aria-label="breadcrumb">
                    <ol className="breadcrumb mb-3">
                        <li className="breadcrumb-item"><Link to="/purchase-requests">Purchase Requests</Link></li>
                        {requestId ? (
                            <>
                                <li className="breadcrumb-item"><Link to={`/purchase_requests/${requestId}`}>Request #{requestId}</Link></li>
                                <li className="breadcrumb-item active">New RFQ</li>
                            </>
                        ) : (
                            <>
                                <li className="breadcrumb-item"><Link to="/rfqs">RFQs</Link></li>
                                <li className="breadcrumb-item active">Create New RFQ</li>
                            </>
                        )}
                    </ol>
                </nav>

                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h2>
                        {form.rfq_type === 'consolidated' ? '🔄 Consolidated' : '📋 Individual'} RFQ
                    </h2>
                    {form.selected_purchase_requests.length > 1 && (
                        <Badge bg="info" className="p-2">
                            Consolidating {form.selected_purchase_requests.length} Purchase Requests
                        </Badge>
                    )}
                </div>

                <form className='mb-5'>
                    {/* Purchase Request Selector - Only show if not coming from specific PR */}
                    {!requestId && (
                        <Card className="mb-4 shadow-sm">
                            <Card.Header className="bg-primary text-white">
                                <h5 className="mb-0">📦 Select Purchase Requests</h5>
                            </Card.Header>
                            <Card.Body>
                                <div className="mb-3">
                                    <label className="form-label fw-bold">
                                        Purchase Requests (Without RFQ) *
                                    </label>
                                    <Select
                                        isMulti
                                        options={availablePurchaseRequests}
                                        value={form.selected_purchase_requests}
                                        onChange={handlePRSelection}
                                        placeholder="Select one or more purchase requests..."
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                    />
                                    <small className="text-muted">
                                        Select multiple requests to consolidate items with same product & quality
                                    </small>
                                </div>

                                {/* Show selected PR details */}
                                {selectedPRDetails.length > 0 && (
                                    <div className="mt-3">
                                        <h6 className="text-primary">Selected Purchase Requests:</h6>
                                        <div className="row g-2">
                                            {selectedPRDetails.map(pr => (
                                                <div key={String(pr._id)} className="col-md-12">
                                                    <Card className="border">
                                                        <Card.Body className="p-3">
                                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                                <div>
                                                                    <strong>{pr.request_no}</strong>
                                                                    <Badge bg="secondary" className="ms-2" style={{ fontSize: '0.7rem' }}>
                                                                        {pr.priority || 'Normal'}
                                                                    </Badge>
                                                                    {/* <div className="text-muted small mb-2">
                                                                        {pr.items?.length || 0} item{(pr.items?.length || 0) !== 1 ? 's' : ''}
                                                                    </div> */}

                                                                    {/* Show items with names and quantities */}
                                                                    {pr.items && pr.items.length > 0 && (
                                                                        <div className="small">
                                                                            <strong>Items:</strong>
                                                                            <div className="mt-1">
                                                                                {pr.items.map((item, index) => (
                                                                                    <div key={index} className="mb-1">
                                                                                        <Badge bg="light" text="dark" className="me-2">
                                                                                            {index + 1}
                                                                                        </Badge>
                                                                                        <strong>{item.product?.productName || 'Unknown Product'}</strong>
                                                                                        {' - '}
                                                                                        <span className="text-primary">
                                                                                            {item.approx_quantity || 0} {item.product.unitId.unitCode || ''} {item.quality ? `(${item.quality})` : ''}
                                                                                        </span>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div className="text-end">
                                                                    <small className="text-muted">
                                                                        {new Date(pr.created_at).toLocaleDateString()}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </Card.Body>
                                                    </Card>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {form.selected_purchase_requests.length === 0 && (
                                    <Alert variant="warning" className="mt-3">
                                        <i className="fas fa-info-circle me-2"></i>
                                        Please select at least one purchase request to continue
                                    </Alert>
                                )}
                            </Card.Body>
                        </Card>
                    )}

                    {/* RFQ Details Form */}
                    {(requestId || form.selected_purchase_requests.length > 0) && (
                        <>
                            <Card className="mb-4 shadow-sm">
                                <Card.Header className="bg-light">
                                    <h5 className="mb-0">📋 RFQ Details</h5>
                                </Card.Header>
                                <Card.Body>
                                    <div className="row g-3">
                                        <div className="col-md-12">
                                            <CompanyAddressSelect
                                                value={form.delivery_location}
                                                onChange={(address) => handleFormChange('delivery_location', address)}
                                                required={true}
                                                showCompanyInfo={true}
                                                allowCustomAddress={true}
                                            />
                                        </div>

                                        <div className="col-sm-6">
                                            <PaymentTermsSelect
                                                value={form.payment_terms}
                                                onChange={(value) => handleFormChange('payment_terms', value)}
                                                required={true}
                                                includeAdvance={true}
                                            />
                                        </div>

                                        <div className="col-sm-6">
                                            <label className="form-label fw-bold">Delivery Date *</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={form.delivery_date}
                                                min={todayDateISO}
                                                onChange={(e) => handleFormChange('delivery_date', e.target.value)}
                                                required
                                            />
                                        </div>

                                        <div className="col-md-12">
                                            <label className="form-label fw-bold">Note</label>
                                            <textarea
                                                className="form-control"
                                                value={form.note}
                                                rows={3}
                                                onChange={(e) => handleFormChange('note', e.target.value)}
                                                placeholder="Additional notes or requirements..."
                                            />
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>

                            {/* RFQ Items */}
                            <Card className="mb-4 shadow-sm">
                                <Card.Header className="bg-light">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <h5 className="mb-0">📦 RFQ Items</h5>
                                        <button 
                                            type="button" 
                                            className="btn btn-sm btn-primary" 
                                            onClick={addItem}
                                        >
                                            <i className="fas fa-plus me-1"></i> Add Item
                                        </button>
                                    </div>
                                </Card.Header>
                                <Card.Body>
                                    {form.items.length === 0 ? (
                                        <Alert variant="info">
                                            <i className="fas fa-info-circle me-2"></i>
                                            No items available. Please add items manually.
                                        </Alert>
                                    ) : (
                                        form.items.map((item, idx) => (
                                            <RFQItemCard
                                                key={item.uid || `item-${idx}`}
                                                item={item}
                                                idx={idx}
                                                onProductSelect={handleProductSelect}
                                                onItemChange={handleItemChange}
                                                onRemove={removeItem}
                                                canRemove={form.items.length > 1}
                                            />
                                        ))
                                    )}
                                </Card.Body>
                            </Card>

                            {/* Vendors Selection */}
                            <Card className="mb-4 shadow-sm">
                                <Card.Header className="bg-light">
                                    <h5 className="mb-0">🏢 Send RFQ To Vendors</h5>
                                </Card.Header>
                                <Card.Body>
                                    {vendors.length === 0 ? (
                                        <Alert variant="warning">No vendors available</Alert>
                                    ) : (
                                        <div className="row">
                                            {vendors.map(v => (
                                                <div className="col-sm-6 col-lg-4 mb-2" key={String(v._id)}>
                                                    <div className="form-check">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            id={`vendor${v._id}`}
                                                            checked={form.vendor_ids.includes(v._id)}
                                                            onChange={() => handleCheckboxChange('vendor_ids', v._id)}
                                                        />
                                                        <label htmlFor={`vendor${v._id}`} className="form-check-label">
                                                            {v.business_name}
                                                        </label>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>

                            {/* Brokers Selection */}
                            <Card className="mb-4 shadow-sm">
                                <Card.Header className="bg-light">
                                    <h5 className="mb-0">🤝 Send RFQ To Brokers</h5>
                                </Card.Header>
                                <Card.Body>
                                    {brokers.length === 0 ? (
                                        <Alert variant="warning">No brokers available</Alert>
                                    ) : (
                                        <div className="row">
                                            {brokers.map(b => (
                                                <div className="col-sm-6 col-lg-4 mb-2" key={String(b._id)}>
                                                    <div className="form-check">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            id={`broker${b._id}`}
                                                            checked={form.broker_ids.includes(b._id)}
                                                            onChange={() => handleCheckboxChange('broker_ids', b._id)}
                                                        />
                                                        <label htmlFor={`broker${b._id}`} className="form-check-label">
                                                            {b.business_name}
                                                        </label>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>

                            {/* Submit Button */}
                            <div className="text-end">
                                <button
                                    className="btn btn-lg btn-success px-5"
                                    type="button"
                                    onClick={() => handleSubmit()}
                                    disabled={submitting}
                                >
                                    {submitting ? 'Creating RFQ...' : 'Submit RFQ' }
                                </button>
                            </div>
                        </>
                    )}
                </form>
            </div>
        </Layout>
    );
};

export default RFQCreatePage;
